import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, CheckCircle2, ShieldAlert, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);
  const token = searchParams.get('token') || '';

  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const tokenMissing = !token;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.length < 6) {
      setError(tt('Password must be at least 6 characters', 'La contraseña debe tener al menos 6 caracteres'));
      return;
    }
    if (pwd !== confirm) {
      setError(tt('Passwords do not match', 'Las contraseñas no coinciden'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setSuccess(true);
      toast.success(tt('Password updated', 'Contraseña actualizada'));
      setTimeout(() => navigate('/auth'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || tt('Reset failed', 'Error al restablecer'));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#0B192C] px-4 py-12" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold">
            <span className="text-[#D4AF37]">Waka</span><span className="text-white">Casa</span>
          </h1>
          <p className="text-sm text-white/50 mt-2">
            {tt('Set a new password', 'Crea una nueva contraseña')}
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 shadow-xl">
          {tokenMissing ? (
            <div className="text-center py-6" data-testid="reset-no-token">
              <ShieldAlert className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <p className="text-white text-sm font-medium">{tt('Invalid link', 'Enlace inválido')}</p>
              <p className="text-white/50 text-xs mt-1">{tt('No reset token in URL.', 'Falta el token en la URL.')}</p>
              <Link to="/auth" className="inline-block mt-4 text-[#D4AF37] text-sm hover:underline" data-testid="reset-back-to-login">
                {tt('Back to sign in', 'Volver a iniciar sesión')}
              </Link>
            </div>
          ) : success ? (
            <div className="text-center py-6" data-testid="reset-success">
              <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
              <p className="text-white text-sm font-medium">{tt('Password updated', 'Contraseña actualizada')}</p>
              <p className="text-white/50 text-xs mt-1">
                {tt('Redirecting to sign in…', 'Redirigiendo al inicio de sesión…')}
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-slate-500">
                  {tt('New password', 'Nueva contraseña')}
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="password" required value={pwd} onChange={e => setPwd(e.target.value)}
                    placeholder={tt('At least 6 characters', 'Al menos 6 caracteres')}
                    className="pl-9 h-11 bg-slate-50" data-testid="reset-password-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">
                  {tt('Confirm password', 'Confirmar contraseña')}
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="password" required value={confirm} onChange={e => setConfirm(e.target.value)}
                    placeholder={tt('Re-type your password', 'Repite tu contraseña')}
                    className="pl-9 h-11 bg-slate-50" data-testid="reset-confirm-input" />
                </div>
              </div>
              {error && (
                <p className="text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" data-testid="reset-error">
                  {error}
                </p>
              )}
              <Button type="submit" disabled={submitting}
                className="w-full h-11 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] rounded-xl font-semibold gap-2"
                data-testid="reset-submit-btn">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {tt('Update password', 'Actualizar contraseña')}
              </Button>
              <Link to="/auth" className="block text-center text-xs text-white/50 hover:text-[#D4AF37]" data-testid="reset-cancel-link">
                {tt('Cancel', 'Cancelar')}
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
